<template>
	<v-container ref="vizContainer">
		<div ref="globeViz" class="lr-globe-viz"></div>
	</v-container>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import ThreeGlobe from "three-globe";
import * as THREE from "three";
import { generateArcsData, generateLablesData, REGIONS } from "./regions";

const props = defineProps<{
	showLines: boolean;
	showRegions: boolean;
	showLabels: boolean;
}>();
const vizContainer = ref<HTMLDivElement | null>(null);
const globeViz = ref<HTMLDivElement | null>(null);

onMounted(async () => {
	let elSize = Math.min(
		(vizContainer.value as any).$el.querySelector(".lr-globe-viz").clientWidth,
		window.innerHeight
	);

	if (!(window as any).alternisGlobeCountriesData) {
		let d = await fetch("/ne_110m_admin_0_countries.geojson").then((res) => res.json());
		(window as any).alternisGlobeCountriesData = d;
	}
	const countries = (window as any).alternisGlobeCountriesData;

	const Globe = new ThreeGlobe()
		// .globeImageUrl("./earth-3.jpg")
		.arcsData(props.showLines ? generateArcsData() : [])
		.arcColor("color")
		.arcDashLength(0.4)
		.arcStroke(1.2)
		.arcDashGap(() => 40 + Math.random() * 40)
		.arcDashInitialGap(() => Math.random() * 80)
		.arcDashAnimateTime(1200)
		.atmosphereColor("#37a3dd")
		.atmosphereAltitude(0.25)
		.polygonsData(countries.features)
		.polygonStrokeColor(() => "#ffffff")
		.polygonSideColor((feature: any) => {
			if (props.showRegions && REGIONS.map((r) => r[0]).includes(feature.properties.NAME)) {
				return "#4db6ac88";
			}
			return "#37a3dd88";
		})
		.polygonCapColor((feature: any) => {
			if (props.showRegions && REGIONS.map((r) => r[0]).includes(feature.properties.NAME)) {
				return "#4db6ac88";
			}
			return "#37a3dd88";
		})
		.polygonAltitude((feature: any) => {
			if (props.showRegions && REGIONS.map((r) => r[0]).includes(feature.properties.NAME)) {
				return 0.1;
			}
			return 0.05;
		})
		.labelsData(props.showLabels ? generateLablesData() : [])
		.labelText((d: any) => d.text)
		.labelSize(1.5)
		.labelDotRadius(0.6)
		.labelAltitude(0.15)
		.labelColor("color");

	// Renderer setup
	const renderer = new THREE.WebGLRenderer();
	renderer.setSize(elSize, elSize);
	renderer.setClearColor(0x000000, 0);
	globeViz.value!.appendChild(renderer.domElement);

	// Scene setup
	const scene = new THREE.Scene();
	scene.add(Globe);

	// Add lighting
	const ambientLight = new THREE.AmbientLight(0xe1f5fe, 0.4);
	scene.add(ambientLight);

	const directionalLight = new THREE.DirectionalLight(0xb3e5fc, 10);
	directionalLight.position.set(5, 5, 5).normalize();
	scene.add(directionalLight);

	// Camera setup
	const camera = new THREE.PerspectiveCamera();

	camera.aspect = 1;
	camera.updateProjectionMatrix();
	camera.position.z = 350;

	// Animation loop
	(function animate() {
		Globe.rotateOnAxis(new THREE.Vector3(0, 1, 0), 0.002);
		directionalLight.rotation.y += 0.06;
		renderer.render(scene, camera);
		requestAnimationFrame(animate);
	})();

	// Resize handling
	window.addEventListener("resize", () => {
		let elSize = Math.min(
			(vizContainer.value as any).$el.querySelector(".lr-globe-viz").clientWidth,
			window.innerHeight
		);
		renderer.setSize(elSize, elSize);
		camera.aspect = 1;
		camera.updateProjectionMatrix();
		camera.position.z = 350;
	});
});
</script>

<style lang="scss">
.lr-globe-viz {
	position: relative;
	margin: 0 auto;
	canvas {
		position: relative;
		margin: 0 auto;
	}
}
</style>
